import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiLoader, FiX, FiInfo, FiZap, FiUpload, FiRotateCcw, FiTrash2, FiSliders } from "react-icons/fi";
import { card, labelClass, Pills } from "./VideoStudioPanel";
import ComparePreview from "./upscale/ComparePreview";
import VideoDropZone from "./videofx/VideoDropZone";
import ResultsList from "./videofx/ResultsList";
import useVideoFx from "./videofx/useVideoFx";
import LookGrid from "./grade/LookGrid";
import Histogram from "./grade/Histogram";
import { supported } from "../video/upscale/gl";
import { upscaleVideo } from "../video/upscale/process";
import { GradeEngine } from "../video/grade/gl";
import { DEFAULTS, LOOKS, RANGES, autoGrade, gradePixel, isNeutral, lookParams, mergeParams } from "../video/grade/grade";
import { parseCube } from "../video/grade/lut";
import { grabFrame, sampleStats } from "../video/grade/measure";
import { clockText, estimateBytes, problemWith, sizeText } from "../video/upscale/plan";
import { recordableFormats } from "../video/timeline";

const makeEngine = (canvas) => new GradeEngine(canvas);
if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") window.__grade = { gradePixel, mergeParams, lookParams };      // (for checking the picture against the sums while developing)

const TABS = [["light", "Light"], ["colour", "Colour"], ["wheels", "Colour wheels"], ["effects", "Effects"], ["lut", "LUT"]];
const SLIDERS = {
  light: [["exposure", "Exposure", "stops"], ["contrast", "Contrast"], ["highlights", "Highlights"], ["shadows", "Shadows"], ["fade", "Faded blacks"]],
  colour: [["temperature", "Temperature (cool ← → warm)"], ["tint", "Tint (green ← → magenta)"], ["saturation", "Saturation"], ["vibrance", "Vibrance"], ["hue", "Hue shift", "°"]],
  effects: [["vignette", "Vignette"], ["grain", "Film grain"], ["sharpen", "Sharpen"]],
};
const WHEELS = [["lift", "Shadows"], ["gamma", "Midtones"], ["gain", "Highlights"]];
const shown = (v, unit) => `${unit === "°" ? Math.round(v) : v.toFixed(2).replace(/\.?0+$/, "") || "0"}${unit === "stops" ? " EV" : unit === "°" ? "°" : ""}`;

function Param({ id, label, unit, value, onChange }) {
  const [min, max, step] = RANGES[id];
  return (
    <label className="block" onDoubleClick={() => onChange(DEFAULTS[id])} title="Double-click to reset">
      <span className="flex items-center justify-between text-xs text-gray-300 mb-1.5">{label}<span className="text-gray-500 tabular-nums">{shown(value, unit)}</span></span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[#22d3ee]" aria-label={label} />
    </label>
  );
}

// AI Video Color Grading: looks, exposure, contrast, white balance, colour wheels, effects and .cube LUTs, with an automatic correction that measures the video. It runs on the
// visitor's own graphics card: the video is never uploaded.
export default function VideoGradeTool() {
  const fx = useVideoFx();
  const { file, opened, fps, busy } = fx;
  const [formats] = useState(() => recordableFormats());
  const [format, setFormat] = useState(0);
  const [params, setParams] = useState(() => mergeParams({}));
  const [lookId, setLookId] = useState("");
  const [tab, setTab] = useState("light");
  const [lut, setLut] = useState(null);                        // { lut, name }
  const [lutError, setLutError] = useState("");
  const [frame, setFrame] = useState(null);
  const [note, setNote] = useState("");
  const [measuring, setMeasuring] = useState(false);

  const webgl = supported();
  const look = useMemo(() => ({ params, lut: lut ? lut.lut : null }), [params, lut]);
  const problem = opened ? problemWith({ w: opened.w, h: opened.h, seconds: opened.seconds, fps: fps || 30, bytes: file?.size || 0 }) : "";
  const changed = !isNeutral(params) || !!lut;

  // a small copy of the frame on screen, for the thumbnails and the histogram: taken when the video is opened and again when it moves
  useEffect(() => {
    if (!opened) { setFrame(null); return undefined; }
    const { video } = opened;
    let last = 0;
    const refresh = () => { const now = performance.now(); if (now - last < 450) return; last = now; setFrame(grabFrame(video, 160)); };
    const first = setTimeout(() => setFrame(grabFrame(video, 160)), 300);
    video.addEventListener("seeked", refresh);
    video.addEventListener("loadeddata", refresh);
    video.addEventListener("timeupdate", refresh);
    return () => { clearTimeout(first); video.removeEventListener("seeked", refresh); video.removeEventListener("loadeddata", refresh); video.removeEventListener("timeupdate", refresh); };
  }, [opened]);

  const setParam = (key, value) => { setLookId(""); setParams((p) => mergeParams({ ...p, [key]: value })); };
  const setWheel = (key, patch) => { setLookId(""); setParams((p) => mergeParams({ ...p, [key]: { ...p[key], ...patch } })); };
  const pickLook = useCallback((id) => {
    setLookId(id || "");
    setNote("");
    setParams((p) => mergeParams({ ...(id ? lookParams(id) : {}), lutStrength: p.lutStrength, intensity: p.intensity }));
  }, []);
  const reset = () => { setLookId(""); setNote(""); setParams(mergeParams({})); setLut(null); setLutError(""); };

  const auto = async () => {
    setMeasuring(true);
    setNote("");
    try {
      const stats = await sampleStats(opened.video, 5);
      if (!stats) throw new Error("The frames of this video could not be read.");
      const p = autoGrade(stats);
      setLookId("");
      setParams((cur) => mergeParams({ ...p, intensity: cur.intensity, lutStrength: cur.lutStrength }));
      const parts = [`exposure ${p.exposure >= 0 ? "+" : ""}${p.exposure.toFixed(2)} EV`, `contrast ${p.contrast >= 0 ? "+" : ""}${Math.round(p.contrast * 100)}%`, `temperature ${p.temperature >= 0 ? "+" : ""}${p.temperature.toFixed(2)}`, `saturation ${Math.round(p.saturation * 100)}%`];
      setNote(`Set from 5 frames of your video: ${parts.join(", ")}. Adjust any slider from here.`);
    } catch (e) {
      setNote(e.message || "The video could not be measured.");
    }
    setMeasuring(false);
  };

  const pickLut = async (chosen) => {
    setLutError("");
    if (!chosen) return;
    try {
      const parsed = parseCube(await chosen.text());
      setLut({ lut: parsed, name: parsed.title || chosen.name });
      setTab("lut");
    } catch (e) {
      setLutError(e.message || "That file could not be read.");
    }
  };

  const start = () => {
    if (!opened || problem || !formats.length) return;
    fx.run(async ({ signal, onProgress, onCanvas }) => ({
      result: await upscaleVideo({ file, size: [opened.w - (opened.w % 2), opened.h - (opened.h % 2)], look, makeEngine, bitrateScale: 1.25, mime: formats[format].mime, fps: fps || 30, onProgress, onCanvas, signal }),
      ext: formats[format].ext, look: lookId ? LOOKS[lookId].label : lut ? "LUT and adjustments" : "Custom grade", extra: lut ? `LUT: ${lut.name}` : "",
    }));
  };
  const sizeOnDisk = opened ? estimateBytes(opened.w, opened.h, fps || 30, opened.seconds) * 1.25 : 0;

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Video Color Grading</h2>
      <p className="text-center text-gray-400 max-w-3xl mx-auto mb-9">
        Give your video a look. Pick a cinematic style, let the tool measure your video and correct its light and colour, or grade by hand with exposure, contrast, white balance, colour wheels and film effects -
        and load your own .cube LUTs. You see the result live against the original, with a histogram. It runs on your own graphics card: your video is never uploaded, and it is free.
      </p>

      {!webgl && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-4 mb-6" role="alert">This browser cannot use the graphics card for video (WebGL 2 is switched off). Use a current Chrome, Edge or Firefox.</p>}
      {webgl && !formats.length && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-4 mb-6" role="alert">This browser cannot save videos. Use a current Chrome, Edge, Firefox or Safari.</p>}

      {!opened ? <VideoDropZone reading={fx.reading} onFile={fx.take} /> : (
        <div className="grid lg:grid-cols-[1.35fr_1fr] gap-6 items-start">
          <div className="space-y-4">
            <div className={card}>
              <div className="flex items-center justify-between gap-3 mb-4">
                <span className="text-sm text-gray-200 truncate" title={file.name}>{file.name} <span className="text-gray-500">· {opened.w}×{opened.h} · {clockText(opened.seconds)}{fps ? ` · ${fps} fps` : ""}</span></span>
                <button type="button" onClick={fx.clear} disabled={!!busy} className="text-xs text-gray-400 hover:text-red-400 inline-flex items-center gap-1 shrink-0 disabled:opacity-40"><FiX size={13} /> Another video</button>
              </div>
              {busy ? (
                <div>
                  <div ref={fx.liveHost} role="img" aria-label="The video being graded" className="rounded-xl bg-black min-h-[200px]" />
                  <p className="text-xs text-gray-500 mt-2 text-center">This is the graded video being saved, frame by frame.</p>
                </div>
              ) : <ComparePreview key={opened.url} opened={opened} out={{ w: opened.w, h: opened.h }} look={look} makeEngine={makeEngine} labels={["Original", "Graded"]} zooms={[1, 2]} />}
            </div>
            <div className={card}>
              <span className={labelClass}>Histogram <span className="normal-case tracking-normal text-gray-500 font-normal">· grey = original, colour = graded</span></span>
              {frame ? <Histogram frame={frame} params={params} lut={look.lut} /> : <p className="text-xs text-gray-500">Reading the frame...</p>}
            </div>
          </div>

          <div className="space-y-4 lg:sticky lg:top-24">
            <div className={`${card} space-y-5`}>
              <div className="flex flex-wrap items-center gap-2.5">
                <button type="button" onClick={auto} disabled={measuring || !!busy} className="px-4 py-2.5 rounded-full bg-brand-gradient text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
                  {measuring ? <><FiLoader className="animate-spin" /> Measuring...</> : <><FiZap size={14} /> Auto-correct</>}
                </button>
                <button type="button" onClick={reset} disabled={!changed} className="px-4 py-2.5 rounded-full border border-white/20 hover:border-accent-light text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40"><FiRotateCcw size={13} /> Reset all</button>
              </div>
              {note && <p className="text-xs text-gray-300 bg-white/5 border border-navy-border rounded-xl px-3.5 py-2.5 flex items-start gap-2"><FiInfo className="mt-0.5 shrink-0 text-accent-light" /> {note}</p>}
              <div>
                <span className={labelClass}>1. A look</span>
                {frame ? <LookGrid frame={frame} selected={lookId} onPick={pickLook} lut={look.lut} lutStrength={params.lutStrength} /> : <p className="text-xs text-gray-500">Reading the frame...</p>}
                {lookId && <p className="text-xs text-gray-400 mt-2.5">{LOOKS[lookId].about}</p>}
                <div className="mt-4"><Param id="intensity" label="Look intensity" value={params.intensity} onChange={(v) => setParam("intensity", v)} /></div>
              </div>
            </div>

            <div className={`${card} space-y-5`}>
              <span className={labelClass}><FiSliders className="inline -mt-0.5" size={12} /> 2. Fine tune</span>
              <Pills label="Section" value={tab} onChange={setTab} options={TABS} />
              {tab !== "wheels" && tab !== "lut" && <div className="space-y-4">{SLIDERS[tab].map(([id, label, unit]) => <Param key={id} id={id} label={label} unit={unit} value={params[id]} onChange={(v) => setParam(id, v)} />)}</div>}
              {tab === "wheels" && (
                <div className="space-y-4">
                  {WHEELS.map(([id, label]) => (
                    <div key={id} className="flex items-center gap-3">
                      <input type="color" value={params[id].hex} onChange={(e) => setWheel(id, { hex: e.target.value, amount: params[id].amount || 0.4 })} aria-label={`${label} colour`} className="w-10 h-10 rounded-full border border-navy-border bg-transparent p-0.5 shrink-0" />
                      <div className="flex-1">
                        <label className="block">
                          <span className="flex items-center justify-between text-xs text-gray-300 mb-1.5">{label}<span className="text-gray-500 tabular-nums">{Math.round(params[id].amount * 100)}%</span></span>
                          <input type="range" min={0} max={1} step={0.02} value={params[id].amount} onChange={(e) => setWheel(id, { amount: Number(e.target.value) })} className="w-full accent-[#22d3ee]" aria-label={`${label} amount`} />
                        </label>
                      </div>
                      <button type="button" onClick={() => setWheel(id, { hex: "#808080", amount: 0 })} title="Reset" aria-label={`Reset ${label}`} className="p-1.5 text-gray-500 hover:text-white"><FiRotateCcw size={13} /></button>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500">Choose a colour to tint the dark, middle or bright parts of the picture with it (teal shadows and orange highlights is the classic cinema look).</p>
                </div>
              )}
              {tab === "lut" && (
                <div className="space-y-3">
                  {lut ? (
                    <>
                      <p className="text-sm text-gray-200 flex items-center justify-between gap-2"><span className="truncate" title={lut.name}>{lut.name} <span className="text-gray-500">· {lut.lut.size}³</span></span>
                        <button type="button" onClick={() => setLut(null)} aria-label="Remove the LUT" className="text-gray-400 hover:text-red-400 shrink-0"><FiTrash2 size={14} /></button></p>
                      <Param id="lutStrength" label="LUT strength" value={params.lutStrength} onChange={(v) => setParams((p) => mergeParams({ ...p, lutStrength: v }))} />
                    </>
                  ) : <p className="text-xs text-gray-400">A LUT is a colour look saved as a .cube file (film emulations, cinematic packs, camera log conversions). It is applied after your other settings.</p>}
                  <label className="px-4 py-2.5 rounded-full border border-white/20 hover:border-accent-light text-sm font-semibold inline-flex items-center gap-2 cursor-pointer"><FiUpload size={14} /> {lut ? "Use another LUT" : "Load a .cube LUT"}
                    <input type="file" accept=".cube,text/plain" className="hidden" onChange={(e) => { pickLut(e.target.files?.[0]); e.target.value = ""; }} /></label>
                  {lutError && <p className="text-xs text-amber-200" role="alert">{lutError}</p>}
                </div>
              )}
              {formats.length > 1 && (
                <div>
                  <span className={labelClass}>3. File type</span>
                  <Pills label="File type" value={String(format)} onChange={(v) => setFormat(Number(v))} options={formats.map((f, i) => [String(i), f.label])} />
                </div>
              )}
            </div>

            <div className={card}>
              {problem && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-4" role="alert">{problem}</p>}
              {busy ? (
                <div>
                  <div className="h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-brand-gradient transition-all" style={{ width: `${Math.round(busy.progress * 100)}%` }} /></div>
                  <p className="text-sm text-gray-200 mt-3 flex items-center gap-2"><FiLoader className="animate-spin text-accent-light" /> Grading... {Math.round(busy.progress * 100)}% · {clockText(busy.progress * opened.seconds)} of {clockText(opened.seconds)}</p>
                  <p className="text-xs text-gray-500 mt-1.5">Keep this tab open and visible: browsers can only record a video while it plays.</p>
                  <button type="button" onClick={() => { fx.cancelRef.current.cancelled = true; }} className="mt-4 px-5 py-2.5 rounded-full border border-white/20 hover:border-red-400 text-sm font-semibold">Stop</button>
                </div>
              ) : (
                <>
                  <button type="button" onClick={start} disabled={!webgl || !formats.length || !!problem || !changed}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed">
                    <FiZap /> Grade the video
                  </button>
                  {!changed && <p className="text-xs text-gray-500 text-center mt-2">Choose a look or move a slider first</p>}
                  <ul className="text-xs text-gray-400 mt-4 space-y-1.5">
                    <li>It takes about as long as the video: <strong className="text-gray-200">{clockText(opened.seconds)}</strong>{opened.seconds > 20 ? "" : " (a short clip is quick)"}.</li>
                    <li>The new file will be about <strong className="text-gray-200">{sizeText(sizeOnDisk)}</strong>, the same size as your video. Your sound is kept as it is.</li>
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {fx.error && <p className="text-sm text-red-200 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mt-6 flex items-start gap-3" role="alert"><FiInfo className="mt-0.5 shrink-0" /> {fx.error}</p>}

      <ResultsList results={fx.results} onRemove={fx.removeResult} suffix={() => "graded"} />

      <div className="mt-10 grid md:grid-cols-3 gap-4 text-sm text-gray-400">
        <div className="rounded-xl bg-white/5 border border-navy-border p-4"><p className="font-semibold text-gray-200 mb-1">What auto-correct does</p>It measures five frames of your video - how bright it is, how much of the range it uses, its colour cast - and sets exposure, contrast, white balance and saturation to correct them, gently. Treat it as a start: a sunset is meant to be orange.</div>
        <div className="rounded-xl bg-white/5 border border-navy-border p-4"><p className="font-semibold text-gray-200 mb-1">Reading the histogram</p>Left is dark, right is bright. A spike at either end means detail is lost to pure black or white. The thumbnails and the histogram show the frame you are on; move the video to see another.</div>
        <div className="rounded-xl bg-white/5 border border-navy-border p-4"><p className="font-semibold text-gray-200 mb-1">Good to know</p>Works on the picture as the browser decodes it (standard video, not log or HDR footage). Everything runs on your computer, so it takes as long as the video, with the tab in front.</div>
      </div>
    </div>
  );
}
